import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTeam } from '../../context/TeamContext';
import Button from '../Common/Button';
import Card from '../Common/Card';

const TeamSetup = ({ onClose }) => {
  const { currentUser } = useAuth();
  const { addTeam } = useTeam();
  const [formData, setFormData] = useState({
    teamName: '',
    role: 'manager',
    managerEmail: '',
    currentFTE: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.teamName.trim()) newErrors.teamName = 'Team name is required';
    if (!formData.managerEmail.trim()) newErrors.managerEmail = 'Manager email is required';
    if (!formData.currentFTE || formData.currentFTE <= 0) newErrors.currentFTE = 'Current FTE count is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const teamData = {
        name: formData.teamName,
        role: formData.role,
        managerEmail: formData.managerEmail,
        currentFTE: parseFloat(formData.currentFTE),
        userName: currentUser.displayName || 'User',
        userEmail: currentUser.email,
        setupComplete: true
      };

      const result = await addTeam(teamData);
      
      if (result.success) {
        onClose();
      } else {
        setErrors({ submit: result.error || 'Failed to create team' });
      }
    } catch (error) {
      setErrors({ submit: error.message || 'An error occurred' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <Card>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Team Setup</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Team Name *
              </label>
              <input
                type="text"
                value={formData.teamName}
                onChange={(e) => handleInputChange('teamName', e.target.value)}
                className={`w-full p-3 border rounded-lg ${errors.teamName ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="e.g., ITE-Unified Communications"
              />
              {errors.teamName && <p className="text-red-500 text-sm mt-1">{errors.teamName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Role *
              </label>
              <select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg"
              >
                <option value="manager">Manager</option>
                <option value="director">Director</option>
                <option value="avp">Associate VP</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Direct Manager Email *
              </label>
              <input
                type="email"
                value={formData.managerEmail}
                onChange={(e) => handleInputChange('managerEmail', e.target.value)}
                className={`w-full p-3 border rounded-lg ${errors.managerEmail ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="manager@company.com"
              />
              {errors.managerEmail && <p className="text-red-500 text-sm mt-1">{errors.managerEmail}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current FTE Count *
              </label>
              <input
                type="number"
                value={formData.currentFTE}
                onChange={(e) => handleInputChange('currentFTE', e.target.value)}
                className={`w-full p-3 border rounded-lg ${errors.currentFTE ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="10"
                min="0"
                step="0.1"
              />
              {errors.currentFTE && <p className="text-red-500 text-sm mt-1">{errors.currentFTE}</p>}
            </div>

            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{errors.submit}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} fullWidth>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} fullWidth>
                {loading ? 'Creating...' : 'Create Team'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default TeamSetup;